'use client'
import { ContainerCustom, Image } from '@/components'
import { HOME_SPACING_SECTION } from '@/constants'
import { Box, Stack, Typography, SvgIcon, SvgIconProps, styled, Skeleton } from '@mui/material'
import React, { useCallback, useState } from 'react'
import FlashSaleTabs from './flash-sale-tabs'
import { URL_IMG } from '@/constants/ui'
import PanelProductsSale from './panel-product-sale'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'
import { useQuery } from '@tanstack/react-query'
import { fetchDailySales } from '@/features/flash-sale/services'
import CountdownFlashSale from '@/features/products/components/product-detail/countdown-flash-sale'

const strokeColor = '#F89B54'
const shadowColor = '#AD4B00'
const textPrimaryColor = '#FFFFFF'
const textSecondaryColor = '#FBFF00'

const textStyle = {
  fontWeight: 900,
  lineHeight: 1.2,
  fontSize: 'inherit',
  // WebkitTextStroke: `1px ${strokeColor}`,
  filter: `drop-shadow(2px 2px 0px ${shadowColor})`,
  letterSpacing: '-0.02em',
  textTransform: 'uppercase',
}

const LightningIcon = (props: SvgIconProps) => (
  <SvgIcon
    {...props}
    viewBox="0 0 24 47"
    sx={{
      width: 'auto',
      height: '1em',
      filter: `drop-shadow(2px 2px 0px ${shadowColor})`,
      ...props.sx,
    }}
  >
    <path
      d="M19.6758 1.79657L11.5996 14.9557H20.1006L19.6865 15.6989L4.05078 43.778L3.12695 43.4216L7.89258 22.9753H0.625L0.761719 22.3659L4.7041 4.79169L4.77148 4.48798L5.0752 4.41473L19.1338 1.04852L20.3076 0.767273L19.6758 1.79657Z"
      fill={textSecondaryColor}
      stroke={strokeColor}
      strokeWidth="1"
    />
  </SvgIcon>
)
// Memoize function để kiểm tra flash sale đang diễn ra
export const isOngoing = (sale: IDailySale) => {
  const now = new Date().getTime()
  const startTime = new Date(sale?.start_date).getTime()
  const endTime = new Date(sale?.end_date).getTime()
  return now >= startTime && now <= endTime
}
export default function SectionFlashSale() {
  const [activeTab, setActiveTab] = useState<number>(0)

  const { data, isLoading } = useQuery({
    queryKey: ['DAILY_SALES'],
    queryFn: () => fetchDailySales({}),
    staleTime: 60 * 1000, // 60 seconds
  })
  const now = Date.now()
  const dailySales =
    data?.items?.sort((a, b) => {
      const startA = new Date(a?.start_date).getTime()
      const endA = new Date(a?.end_date).getTime()
      const startB = new Date(b?.start_date).getTime()
      const endB = new Date(b?.end_date).getTime()

      const isRunningA = startA <= now && now <= endA
      const isRunningB = startB <= now && now <= endB

      // 1. Đang diễn ra thì lên trước
      if (isRunningA && !isRunningB) return -1
      if (!isRunningA && isRunningB) return 1

      // 2. Cùng trạng thái → sort theo duration (ngắn hơn đứng trước)
      const durationA = endA - startA
      const durationB = endB - startB

      return durationA - durationB
    }) || []
  const isOutDate = (sale: IDailySale) => {
    const now = new Date().getTime()
    const endTime = new Date(sale?.end_date).getTime()
    return now > endTime
  }
  const activeSale = dailySales[activeTab]
  // Memoize handler để tránh re-render các tabs
  const handleTabClick = useCallback((index: number) => {
    setActiveTab(index)
  }, [])

  if (dailySales.length === 0 && !isLoading) {
    return null
  }

  return (
    <Box component="section" position={'relative'} bgcolor={'#F46A00'}>
      <Box
        position={'absolute'}
        top={0}
        zIndex={1}
        left={0}
        width={'100%'}
        height={'100%'}
        sx={{ mixBlendMode: 'soft-light' }}
      >
        <Image
          src={URL_IMG + 'bg-sale.jpg'}
          alt="Flash Sale Banner"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>
      <ContainerCustom component={'div'}>
        <Box
          sx={{
            py: HOME_SPACING_SECTION,
          }}
          position={'relative'}
          zIndex={2}
        >
          <Stack>
            <Stack
              sx={{
                flexDirection: {
                  xs: 'column',
                  lg: 'row',
                },
                gap: 2,
                alignItems: 'flex-end',
              }}
            >
              {/* Text Flash Sale */}
              {isLoading ? (
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                  alignItems="center"
                  sx={{ height: { xs: '36px', md: '48px' } }}
                >
                  <Skeleton
                    variant="circular"
                    width={30}
                    height={30}
                    sx={{ bgcolor: 'rgba(255,255,255,0.4)' }}
                  />
                  <Skeleton
                    variant="text"
                    width={250}
                    height={40}
                    sx={{ bgcolor: 'rgba(255,255,255,0.4)' }}
                  />
                </Stack>
              ) : (
                <Stack
                  direction={'row'}
                  gap={2}
                  fontSize={{ xs: '28px', md: '48px' }}
                  alignItems={'center'}
                  justifyContent={{
                    xs: 'center',
                    md: 'unset',
                  }}
                  flexWrap={'wrap'}
                  width={'100%'}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={{ xs: 'center', md: 'flex-start' }}
                    spacing={0.5}
                  >
                    <LightningIcon sx={{ fontSize: 'inherit' }} />
                    <Typography sx={{ ...textStyle, color: textSecondaryColor }}>
                      {activeSale.title}
                    </Typography>
                  </Stack>
                  <CountdownFlashSale
                    endDate={activeSale.end_date}
                    textStyle={{
                      alignItems: { xs: 'flex-end', md: 'center', flexDirection: 'row', gap: 4 },
                      '& .text-sale': {
                        fontSize: { xs: '16px', md: '18px' },
                        fontWeight: 600,
                        color: '#fff',
                      },
                    }}
                    colorDot="white"
                    // timeBoxSx={{ backgroundColor: '#fff' }}
                    // timeBoxTextProps={{ color: '#000' }}
                  />
                </Stack>
              )}
              {/* Nav time*/}
              <Box
                sx={{
                  width: {
                    xs: '100%',
                    md: 'auto',
                  },
                  background: {
                    xs: '#fff',
                    md: 'transparent',
                  },
                  ml: 'auto',
                }}
              >
                <Swiper
                  slidesPerView={'auto'}
                  modules={[Pagination]}
                  pagination={{ type: 'progressbar', el: '.swiper-progressbar-pagination' }}
                >
                  {isLoading
                    ? Array.from({ length: 1 }).map((_, index) => (
                        <SwiperSlideTabStyle key={`skeleton-${index}`}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Skeleton
                              variant="text"
                              width={60}
                              height={24}
                              sx={{ bgcolor: 'rgba(255,255,255,0.4)' }}
                            />
                            <Skeleton
                              variant="text"
                              width={60}
                              height={24}
                              sx={{ bgcolor: 'rgba(255,255,255,0.4)' }}
                            />
                          </Stack>
                          <Skeleton
                            variant="text"
                            width={120}
                            height={24}
                            sx={{ bgcolor: 'rgba(255,255,255,0.4)' }}
                          />
                        </SwiperSlideTabStyle>
                      ))
                    : dailySales.map((sale, index) => {
                        const ongoing = isOngoing(sale)
                        const outDate = isOutDate(sale)
                        const isActive = index === activeTab
                        if (outDate) return null
                        return (
                          <SwiperSlideTabStyle key={sale.entity_id}>
                            <FlashSaleTabs
                              sale={sale}
                              index={index}
                              isActive={isActive}
                              isOngoing={ongoing}
                              onClick={() => handleTabClick(index)}
                            />
                          </SwiperSlideTabStyle>
                        )
                      })}
                  <SwiperPaginationStyle className="swiper-progressbar-pagination" />
                </Swiper>
              </Box>
            </Stack>
            <WrapperSale>
              <PanelProductsSale sale={activeSale} isLoading={isLoading} />
            </WrapperSale>
          </Stack>
        </Box>
      </ContainerCustom>
    </Box>
  )
}
const SwiperSlideTabStyle = styled(SwiperSlide)(() => ({
  width: 'fit-content',
  '@media (max-width: 768px)': {
    width: '50%',
  },
}))
const WrapperSale = styled(Box)(() => ({
  position: 'relative',
  padding: 16,
  backgroundColor: '#fff',
  '@media (max-width: 768px)': {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
}))
const SwiperPaginationStyle = styled('div')(() => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  zIndex: 10,
  height: '2px !important',
  top: 'unset !important',
  backgroundColor: 'rgba(0,0,0,0.1) ',
  '.swiper-pagination-progressbar-fill': {
    backgroundColor: '#C84B00',
  },
}))
